import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useAuth } from '@/hooks/auth';
import { useToast } from '@/hooks/use-toast';

import WelcomeStep from './steps/WelcomeStep';
import ProfileStep from './steps/ProfileStep';
import SkillsStep from './steps/SkillsStep';
import PortfolioStep from './steps/PortfolioStep';
import CompletionStep from './steps/CompletionStep';
import ProfilePictureStep from './steps/ProfilePictureStep';

const OnboardingFlow: React.FC = () => {
  const { user } = useAuth();
  const { currentStep, updateOnboardingStatus, completeOnboarding, skipOnboarding } = useOnboarding();
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const userMetadata = useMemo(
    () => ((user?.user_metadata ?? {}) as Record<string, string | undefined>),
    [user?.user_metadata]
  );
  const userName =
    userMetadata.full_name ||
    userMetadata.display_name ||
    userMetadata.name ||
    userMetadata.username ||
    undefined;

  const totalSteps = 5; // 1 = welcome, 2+ = actual onboarding steps

  const handleNext = async () => {
    if (currentStep < totalSteps) {
      setIsLoading(true);
      try {
        await updateOnboardingStatus('in_progress', (currentStep + 1) as any);
      } catch (error) {
        console.error('Error updating onboarding step:', error);
        toast({
          title: "Error",
          description: "Failed to update progress. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePrevious = async () => {
    if (currentStep > 1) {
      setIsLoading(true);
      try {
        await updateOnboardingStatus('in_progress', (currentStep - 1) as any);
      } catch (error) {
        console.error('Error updating onboarding step:', error);
        toast({
          title: "Error",
          description: "Failed to update progress. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSkip = async () => {
    setIsLoading(true);
    try {
      await skipOnboarding();
      toast({
        title: "Onboarding skipped",
        description: "You can complete your profile later from the dashboard.",
      });
      // navigate('/dashboard'); // This line is removed as per the edit hint
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      toast({
        title: "Error",
        description: "Failed to skip onboarding. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      await completeOnboarding();
      toast({
        title: "Welcome!",
        description: "Your profile is now complete. Let's get started!",
      });
      setShouldRedirect(true);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast({
        title: "Error",
        description: "Failed to complete onboarding. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep onNext={handleNext} />;
      case 2:
        return <ProfileStep onNext={handleNext} onPrevious={handlePrevious} />;
      case 3:
        return <SkillsStep onNext={handleNext} onPrevious={handlePrevious} />;
      case 4:
        return <ProfilePictureStep onNext={handleNext} onPrevious={handlePrevious} />;
      case 5:
        return <CompletionStep onComplete={handleComplete} onPrevious={handlePrevious} />;
      default:
        return <WelcomeStep onNext={handleNext} />;
    }
  };

  if (shouldRedirect) {
    return <Navigate to="/dashboard" replace />;
  }

  return renderStep();
};

export default OnboardingFlow;
